import moment from 'moment';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { ActionItemBucket, bucketForDueDate } from '../../../web/pages/ActionItems/utils/buckets';
import { ILeadSearchView } from '../../viewModels/leads/interfaces';
import { FollowUpImportance, ILeadFollowUp } from '../../viewModels/queue';

export type FollowUpBucket = Exclude<ActionItemBucket, 'noDate'>;

interface IFollowUpSection {
	bucket: FollowUpBucket;
	label: string;
	items: ILeadSearchView[];
}

const FOLLOW_UP_BUCKETS: { bucket: FollowUpBucket; label: string }[] = [
	{ bucket: 'overdue', label: 'Overdue' },
	{ bucket: 'today', label: 'Today' },
	{ bucket: 'tomorrow', label: 'Tomorrow' },
	{ bucket: 'thisWeek', label: 'This Week' },
	{ bucket: 'later', label: 'Later' },
];

// The API stores a date-only follow up at midnight, so midnight means "no time of day".
const isMidnight = (date: moment.Moment) => date.hours() === 0 && date.minutes() === 0 && date.seconds() === 0;

export function formatFollowUpDate({ followUpDate, scheduled }: ILeadFollowUp = {}, now = moment()): string {
	if (!followUpDate) {
		return 'No follow up';
	}
	const due = moment(followUpDate);
	const time = isMidnight(due) ? '' : ` ${due.format('h:mm A')}`;
	if (!scheduled) {
		if (due.isSame(now, 'day')) {
			return `Today${time}`;
		}
		if (due.isSame(now.clone().add(1, 'day'), 'day')) {
			return `Tomorrow${time}`;
		}
	}
	return `${due.format('M/DD/YY')}${time}`;
}

// Cursor pages can repeat a lead when its follow up is rescheduled between fetches; keep its first appearance.
export function uniqueFollowUps(items: ILeadSearchView[]): ILeadSearchView[] {
	const seen = new Set<string>();
	return items.filter(item => {
		const companyId = item.company?.id;
		if (!companyId) {
			return true;
		}
		if (seen.has(companyId)) {
			return false;
		}
		seen.add(companyId);
		return true;
	});
}

export function groupFollowUps(items: ILeadSearchView[], now: Date): IFollowUpSection[] {
	const byBucket = new Map<FollowUpBucket, ILeadSearchView[]>();
	for (const item of items) {
		const due = item.followUp?.followUpDate ? moment(item.followUp.followUpDate) : null;
		const bucket = due ? (bucketForDueDate(due.toDate(), !isMidnight(due), now) as FollowUpBucket) : 'later';
		byBucket.set(bucket, [...(byBucket.get(bucket) ?? []), item]);
	}
	return FOLLOW_UP_BUCKETS.filter(({ bucket }) => byBucket.has(bucket)).map(section => ({
		...section,
		items: byBucket.get(section.bucket) ?? [],
	}));
}

export const pepperColor = (importance?: FollowUpImportance) =>
	importance === FollowUpImportance.High
		? THEME.colors.danger.dark
		: importance === FollowUpImportance.Medium
			? THEME.colors.warning.main
			: THEME.colors.success.main;
